import "./styles.css";
import { i18nReady } from "./i18n";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { auth } from "./lib/auth";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient, auth },
  // Start loading a route's code and data when a link is hovered or focused.
  defaultPreload: "intent",
  // Let TanStack Query own freshness; the router just triggers loads.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

// Re-run route guards when the session changes (sign-in, sign-out, expiry).
let lastUser = auth.get()?.user.id ?? null;
auth.subscribe(() => {
  const user = auth.get()?.user.id ?? null;
  if (user !== lastUser) {
    lastUser = user;
    if (!user) queryClient.clear();
    void router.invalidate();
  }
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Polish texts are a separate chunk: render once the start language is in.
void i18nReady.then(() =>
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  ),
);
